import type { Task } from "idfweb-tasks";
import { mkdirp, rmTree, type Vfs } from "idfweb-vfs";

import { defaultToolchain, type ToolchainName } from "./chips.ts";
import { TarReader, type TarEntry } from "./tar.ts";

/**
 * The asset set as it reaches a browser: an index and a handful of gzipped tar
 * archives, fetched and unpacked into the filesystem the build will run over.
 *
 * The same layout as the directory the CLI reads — `bin/clang`, `idf/...` — so
 * the two cannot drift and a guest path means one thing on both. What differs
 * is only how the bytes arrive.
 *
 * Nothing here is browser-only: `fetch`, `DecompressionStream` and the Vfs
 * interface all exist under Node, which is what lets this code path be run and
 * checked from the CLI rather than only from a page. See `idfweb web-check`.
 */

/** What `scripts/pack-assets.mjs` writes next to the archives. */
export interface AssetIndex {
    /** Where in the guest the archives' paths are relative to, e.g. "/usr". */
    root: string;
    /** The chip targets of the bundled ESP-IDF: idf.py's supported and preview lists. */
    targets: { supported: string[]; preview: string[] };
    /** The toolchain each chip builds with by default, from `share/idfweb/targets.json`. */
    toolchains: Record<string, ToolchainName>;
    archives: ArchiveInfo[];
}

export interface ArchiveInfo {
    /** What to show a person: the directory it holds, e.g. "idf/components/bt". */
    name: string;
    /** The file to fetch, relative to the index. */
    file: string;
    /** Compressed size, which is what a download's progress is measured in. */
    bytes: number;
    /** Uncompressed size, and how many files are in it. */
    rawBytes: number;
    files: number;
    /** The chips this archive is for. Absent on an archive every chip needs. */
    targets?: string[];
    /** The directories it covers, removed when its chips are deselected. */
    paths?: string[];
    /** The one toolchain that uses it. Absent on an archive both use. */
    toolchain?: ToolchainName;
    /** The chips whose ULP builds with it, whatever their toolchain. */
    ulpTargets?: string[];
}

/**
 * The archives of a set that are unpacked in one filesystem.
 *
 * The caller keeps it and gives it back on the next call. Each archive is
 * recorded as it arrives, so a fetch that fails halfway leaves an accurate
 * record of what was unpacked.
 */
export interface UnpackedAssets {
    /** The index these archives came from, fetched once. */
    readonly index: AssetIndex;
    /** The chip the filesystem is laid out for, or undefined for the whole set. */
    target: string | undefined;
    /** The toolchain it is laid out for, or undefined for both. */
    toolchain: ToolchainName | undefined;
    /** Whether it also holds what the chip's ULP builds with. */
    ulp?: boolean;
    /** The archives unpacked here, by file name. */
    readonly files: Set<string>;
}

/**
 * A filesystem that can also be given the symlinks the set contains.
 *
 * `symlink` is not on Vfs because nothing in the guest creates one — the WASIX
 * calls only resolve and read them — and this is the one caller that does. It
 * is MemVfs's method, and MemVfs is what a page unpacks into.
 */
export type UnpackVfs = Vfs & { symlink(target: string, path: string): void };

/**
 * An ESP-IDF release a site offers besides its default set, e.g. "v6.1": the
 * name build-tools/versions.sh gives it. Its index sits beside the default
 * one in the same archive directory, and its examples beside the default
 * examples.
 */
const RELEASE = /^[A-Za-z0-9][A-Za-z0-9._-]*$/;

/** The index of a release's set, or of the default set without one. */
export function assetIndexFile(release?: string): string {
    return release ? `index-${checkRelease(release)}.json` : "index.json";
}

/** The site directory of a release's ESP-IDF examples, "idf-examples" without one. */
export function idfExamplesDir(release?: string): string {
    return release ? `idf-examples-${checkRelease(release)}` : "idf-examples";
}

/** Whether `release` can name a release: letters, digits, dots, dashes and underscores. */
export function isReleaseName(release: string): boolean {
    return RELEASE.test(release);
}

function checkRelease(release: string): string {
    if (!isReleaseName(release)) throw new Error(`"${release}" is not an ESP-IDF release name`);
    return release;
}

export interface FetchOptions {
    /** Where the index and the archives are, e.g. "/assets". */
    baseUrl: string;
    /** The index's file under `baseUrl`, "index.json" unless a release's: see assetIndexFile. */
    index?: string | undefined;
    vfs: UnpackVfs;
    /** The chip to lay the filesystem out for. Without one, every archive. */
    target?: string | undefined;
    /**
     * The toolchain to lay the filesystem out for. Without one, the chip's
     * default in the index; without a chip, both.
     */
    toolchain?: ToolchainName | undefined;
    /**
     * Also lay out what the chip's RISC-V ULP or LP core builds with:
     * riscv32-esp-elf GCC and the ULP's multilib, which a project that
     * enables a ULP needs whatever the chip's own toolchain is.
     */
    ulp?: boolean | undefined;
    /** What a previous call left in this filesystem. */
    have?: UnpackedAssets | undefined;
    /**
     * How many archives to have in flight at once.
     *
     * More than one because a single connection does not fill the link and
     * unpacking one archive leaves the socket idle; not many more because
     * every one of them is writing into the same filesystem lock.
     */
    concurrency?: number;
    /**
     * The task this download is part of, counted in compressed bytes. Each
     * archive in flight becomes a task under it; the caller ends this one.
     */
    task?: Task | undefined;
    signal?: AbortSignal | undefined;
}

export async function fetchAssetIndex(
    baseUrl: string,
    signal?: AbortSignal,
    index = "index.json",
): Promise<AssetIndex> {
    const response = await fetch(`${baseUrl}/${index}`, { signal });
    if (!response.ok) {
        throw new Error(`no asset index at ${baseUrl}/${index} (${response.status})`);
    }
    return (await response.json()) as AssetIndex;
}

/**
 * Fetch the archives this chip needs, unpack them into `vfs`, and remove the
 * directories of the archives it does not need.
 *
 * The archives are unpacked as they arrive rather than downloaded and then
 * unpacked: a gigabyte held twice is a gigabyte that need not be, and the
 * progress a person sees is then the real one. What the chip does not need is
 * removed first, because the filesystem has room for one chip and a build
 * tree. An archive already unpacked is not fetched again.
 */
export async function fetchAssetSet(opts: FetchOptions): Promise<UnpackedAssets> {
    const index = opts.have?.index ?? (await fetchAssetIndex(opts.baseUrl, opts.signal, opts.index));
    const state: UnpackedAssets = opts.have ?? {
        index,
        target: undefined,
        toolchain: undefined,
        files: new Set(),
    };
    const { toolchain, ulp, wanted } = selection(index, opts);
    const keep = new Set(wanted.map((archive) => archive.file));

    mkdirp(opts.vfs, index.root);
    state.target = opts.target;
    state.toolchain = toolchain;
    state.ulp = ulp;
    // Gathered first: a chip's multilib variant is split over several archives,
    // which list the same directory.
    const dropping = new Set<string>();
    for (const archive of index.archives) {
        if (!state.files.has(archive.file) || keep.has(archive.file)) continue;
        for (const path of archive.paths ?? []) dropping.add(`${index.root}/${path}`);
        state.files.delete(archive.file);
    }
    // A directory left by a fetch that failed halfway may be gone already. A
    // symlink whose target was dropped with another archive is removed too.
    for (const path of dropping) if (present(opts.vfs, path)) rmTree(opts.vfs, path);

    // Largest first, so that the long pole starts at the beginning rather than
    // being left to run on its own at the end.
    const queue = wanted.filter((archive) => !state.files.has(archive.file)).sort((a, b) => b.bytes - a.bytes);
    const totalAll = queue.reduce((n, a) => n + a.bytes, 0);
    // Counted first: the workers shift entries off the queue.
    const archives = queue.length;
    const received = new Map<string, number>();
    let doneArchives = 0;

    const report = () => {
        opts.task?.progress(
            [...received.values()].reduce((n, v) => n + v, 0),
            { total: totalAll, detail: `${doneArchives} of ${archives} archives unpacked` },
        );
    };

    const workers = Array.from({ length: opts.concurrency ?? 3 }, async () => {
        for (let archive = queue.shift(); archive !== undefined; archive = queue.shift()) {
            received.set(archive.name, 0);
            const task = opts.task?.task(archive.name, { total: archive.bytes, unit: "bytes" });
            report();
            try {
                await fetchArchive(opts, index.root, archive, (n) => {
                    received.set(archive.name, n);
                    task?.progress(n);
                    report();
                });
            } catch (e) {
                task?.failed(e instanceof Error ? e.message : String(e));
                throw e;
            }
            doneArchives++;
            state.files.add(archive.file);
            received.set(archive.name, archive.bytes);
            task?.done();
            report();
        }
    });
    await Promise.all(workers);
    return state;
}

/** Whether anything is at `path`, a dangling symlink included. */
function present(vfs: Vfs, path: string): boolean {
    try {
        vfs.stat(path, false);
        return true;
    } catch {
        return false;
    }
}

/** The toolchain a chip builds with unless a build sets another. */
/**
 * The archives `fetchAssetSet` with these options would download, given what
 * `have` holds: empty when it would only drop archives the chip no longer needs.
 */
export function archivesToFetch(
    have: UnpackedAssets,
    opts: Pick<FetchOptions, "target" | "toolchain" | "ulp">,
): ArchiveInfo[] {
    return selection(have.index, opts).wanted.filter((archive) => !have.files.has(archive.file));
}

function selection(index: AssetIndex, opts: Pick<FetchOptions, "target" | "toolchain" | "ulp">) {
    const toolchain = opts.target === undefined ? undefined : (opts.toolchain ?? chipToolchain(index, opts.target));
    const ulp = opts.target !== undefined && opts.ulp === true;
    const wanted = index.archives.filter((archive) => serves(archive, opts.target, toolchain, ulp));
    return { toolchain, ulp, wanted };
}

/**
 * The toolchain `target` builds with: `chosen` when the set has archives of
 * it for the chip, else the chip's default, which is the one a build without
 * a choice takes from `share/idfweb/targets.json`.
 */
export function chipToolchain(index: AssetIndex, target: string, chosen?: ToolchainName): ToolchainName {
    const offered = index.archives.some((a) => a.toolchain === chosen && a.targets?.includes(target));
    return chosen !== undefined && offered ? chosen : defaultToolchain(index.toolchains, target);
}

/** Whether a filesystem laid out for this chip, toolchain and ULP choice holds this archive. */
function serves(
    archive: ArchiveInfo,
    target: string | undefined,
    toolchain: ToolchainName | undefined,
    ulp: boolean,
): boolean {
    if (ulp && target !== undefined && archive.ulpTargets?.includes(target)) return true;
    if (toolchain !== undefined && archive.toolchain !== undefined && archive.toolchain !== toolchain) return false;
    return archive.targets === undefined || target === undefined || archive.targets.includes(target);
}

async function fetchArchive(
    opts: FetchOptions,
    root: string,
    archive: ArchiveInfo,
    onBytes: (received: number) => void,
): Promise<void> {
    const response = await fetch(`${opts.baseUrl}/${archive.file}`, { signal: opts.signal });
    if (!response.ok || response.body === null) {
        throw new Error(`cannot fetch ${archive.file} (${response.status})`);
    }

    // Counted before the gunzip, because the compressed size is what the index
    // knows and what the network is actually moving. The browser must not have
    // decompressed it for us on the way — the archives are served as
    // application/octet-stream for exactly that reason.
    let received = 0;
    const counted = new TransformStream<Uint8Array, Uint8Array>({
        transform(chunk, controller) {
            received += chunk.byteLength;
            onBytes(received);
            controller.enqueue(chunk);
        },
    });

    const unpacker = new Unpacker(opts.vfs, root);
    const reader = new TarReader(unpacker);
    // The casts are the DOM types disagreeing with themselves — a
    // DecompressionStream is declared to take a BufferSource and to be
    // iterable nowhere — rather than anything uncertain about what arrives.
    const gunzip = new DecompressionStream("gzip") as unknown as ReadableWritablePair<Uint8Array, Uint8Array>;
    const stream = response.body.pipeThrough(counted).pipeThrough(gunzip);
    for await (const chunk of stream as unknown as AsyncIterable<Uint8Array>) {
        reader.push(chunk);
    }
    reader.end();
}

/**
 * A tar sink that writes into a filesystem.
 *
 * One open handle at a time, because a tar holds one file at a time. The
 * directories are created when referenced by archive paths: an archive
 * carries its directory entries, but an archive holding only part of a tree
 * still has to land somewhere.
 */
class Unpacker {
    readonly #vfs: UnpackVfs;
    readonly #root: string;
    #handle: number | undefined;

    constructor(vfs: UnpackVfs, root: string) {
        this.#vfs = vfs;
        this.#root = root;
    }

    entry(entry: TarEntry): void {
        const path = `${this.#root}/${entry.path}`;
        if (entry.kind === "dir") {
            mkdirp(this.#vfs, path);
            return;
        }
        mkdirp(this.#vfs, parentOf(path));
        if (entry.kind === "symlink") {
            this.#vfs.symlink(entry.target, path);
            return;
        }
        this.#handle = this.#vfs.open(path, {
            read: false,
            write: true,
            create: true,
            exclusive: false,
            truncate: true,
            append: false,
        });
        // The whole file at once, before a byte of it has arrived. A tar says
        // how big each file is, and a filesystem in one arena grows a file by
        // doubling and freeing what it outgrew — so a 60 MB binary written in
        // 64 KB pieces would leave a dozen dead blocks behind it, and a
        // gigabyte of assets would not fit in two.
        if (entry.size > 0) this.#vfs.truncate(this.#handle, entry.size);
    }

    data(chunk: Uint8Array, offset: number): void {
        if (this.#handle === undefined) throw new Error("file data outside a file");
        for (let at = 0; at < chunk.length;) {
            at += this.#vfs.write(this.#handle, chunk.subarray(at), offset + at);
        }
    }

    endOfEntry(entry: TarEntry): void {
        if (this.#handle !== undefined) {
            this.#vfs.close(this.#handle);
            this.#handle = undefined;
        }
        // After the close, so that it is not the write that sets it back. A
        // symlink is skipped: setting times through one would touch its target.
        if (entry.kind === "file") {
            this.#vfs.setTimes(`${this.#root}/${entry.path}`, entry.mtimeMs, entry.mtimeMs, true);
        }
    }
}

function parentOf(path: string): string {
    return path.slice(0, path.lastIndexOf("/")) || "/";
}
